import { useRouter } from 'next/router';
import { getDataFromTree } from '@apollo/client/react/ssr';
// Types
import { NextPage } from 'next';
import { UserRole } from 'api/graphql-global-types';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import { useGetStoreAmas } from 'hooks';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
import StripeElements from 'layouts/StripeElements/StripeElements';
// Components
import OrderAma from 'components/OrderAma/OrderAma';
// Helpers
import { generateAmaStructuredData } from 'helpers/structured-data';
import { useEffect } from 'react';

const OrderAmaPage: NextPage = () => {
  const router = useRouter();
  const { id } = router.query;

  const { data, loading } = useGetStoreAmas({
    variables: {
      input: { slug: id as string },
    },
  });

  const store = data?.store;
  const storeName = `${store?.firstName || ''} ${store?.lastName || ''}`;
  const storeAvatarUrl = store?.storeDetails?.avatarURL || '';
  const title =
    store?.role === UserRole.Athlete
      ? `Order AMA from Fighter ${storeName}`
      : `Order AMA from ${storeName}`;

  const isOrganization = store?.role === UserRole.Organization;
  const hasAmas = Boolean(store?.amas?.length);
  // Only a store that actually offers AMAs (and isn't an organization) has
  // real content here. Anything else stays noindex so we never advertise an
  // empty/redirecting page to Google.
  const isIndexable = hasAmas && !isOrganization;
  const structuredData = isIndexable
    ? generateAmaStructuredData(store, store?.amas?.[0])
    : undefined;

  useEffect(() => {
    const hasNoAmas = !store?.amas?.length;

    if (!loading && (hasNoAmas || isOrganization)) {
      router.push(`/${id}`);
    }
  }, [id, router, store, loading, isOrganization]);

  return (
    <>
      <Head
        title={title}
        description={`Place order for a custom "Ask me anything" by ${storeName}`}
        image={storeAvatarUrl}
        robots={isIndexable ? 'index, follow' : 'noindex, follow'}
        structuredData={structuredData}
      />
      <Header />
      <Main>
        <StripeElements>
          <OrderAma store={data?.store} loading={loading} />
        </StripeElements>
      </Main>
      <Footer />
    </>
  );
};

export default withApollo(OrderAmaPage, { getDataFromTree });
